import {
  getFridgeHumidityHistory,
  getFridges,
  getFridgeTemperatureHistory,
  getTunnelHumidityHistory,
  getTunnels,
  getTunnelTemperatureHistory,
  updateFridgeNotificationSettings,
  updateTunnelNotificationSettings,
} from '@/api/frigo';
import type { ColdStorageKind, NotificationSettingsPayload } from '@/types/frigo';
import { useMutation, useQuery } from '@tanstack/react-query';

export function useTunnels({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: ['tunnels'],
    queryFn: getTunnels,
    enabled,
    staleTime: 0,
  });

  return { query };
}

export function useFridges({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: ['fridges'],
    queryFn: getFridges,
    enabled,
    staleTime: 0,
  });

  return { query };
}

export function useColdStorageHistory({
  id,
  kind,
  temperatureStartDate,
  temperatureEndDate,
  humidityStartDate,
  humidityEndDate,
  enabled,
}: {
  id: string;
  kind: ColdStorageKind | null;
  temperatureStartDate: string;
  temperatureEndDate: string;
  humidityStartDate: string;
  humidityEndDate: string;
  enabled?: boolean;
}) {
  const canFetch = Boolean(id && kind) && enabled !== false;

  const temperatureQuery = useQuery({
    queryKey: ['cold-storage-temperature-history', kind, id, temperatureStartDate, temperatureEndDate],
    queryFn: () =>
      kind === 'fridge'
        ? getFridgeTemperatureHistory({ fridgeId: id, startDate: temperatureStartDate, endDate: temperatureEndDate })
        : getTunnelTemperatureHistory({ tunnelId: id, startDate: temperatureStartDate, endDate: temperatureEndDate }),
    enabled: canFetch,
    staleTime: 0,
  });

  const humidityQuery = useQuery({
    queryKey: ['cold-storage-humidity-history', kind, id, humidityStartDate, humidityEndDate],
    queryFn: () =>
      kind === 'fridge'
        ? getFridgeHumidityHistory({ fridgeId: id, startDate: humidityStartDate, endDate: humidityEndDate })
        : getTunnelHumidityHistory({ tunnelId: id, startDate: humidityStartDate, endDate: humidityEndDate }),
    enabled: canFetch,
    staleTime: 0,
  });

  return { temperatureQuery, humidityQuery };
}

export function useUpdateColdStorageNotificationSettings({
  id,
  kind,
}: {
  id: string;
  kind: ColdStorageKind;
}) {
  const mutation = useMutation({
    mutationFn: (payload: NotificationSettingsPayload) =>
      kind === 'fridge'
        ? updateFridgeNotificationSettings({ fridgeId: id, payload })
        : updateTunnelNotificationSettings({ tunnelId: id, payload }),
  });

  return { mutation };
}
